import { z } from "zod";

/**
 * Sprungmarken: Paare aus Quelle (↩, wo man springt) und Ziel (★, wohin).
 * Koordinaten sind Prozentwerte (0–100) relativ zur Seite.
 */
export const jumpPointSchema = z.object({
  pageNumber: z.number().int().min(1).max(99_999),
  x: z.number().min(0).max(100),
  y: z.number().min(0).max(100),
});

export const jumpMarkerSchema = z.object({
  id: z.string().trim().min(1).max(64),
  label: z.string().trim().min(1).max(80),
  source: jumpPointSchema.nullable(),
  target: jumpPointSchema.nullable(),
});

export const jumpMarkersSchema = z.array(jumpMarkerSchema).max(200);

export type JumpPoint = z.infer<typeof jumpPointSchema>;
export type JumpMarker = z.infer<typeof jumpMarkerSchema>;

type Role = "source" | "target";

const round3 = (value: number) => Math.round(value * 1000) / 1000;

function clampPercent(value: unknown, fallback: number): number {
  const num = Number(value);
  if (!Number.isFinite(num)) return fallback;
  return round3(Math.max(0, Math.min(100, num)));
}

function normalizePoint(point: unknown, role: Role): JumpPoint | null {
  if (!point || typeof point !== "object") return null;
  const raw = point as Record<string, unknown>;
  const page = Number(raw.pageNumber);
  if (!Number.isFinite(page) || page < 1) return null;
  const defaults = role === "target" ? { x: 70, y: 25 } : { x: 30, y: 25 };
  return {
    pageNumber: Math.floor(page),
    x: clampPercent(raw.x, defaults.x),
    y: clampPercent(raw.y, defaults.y),
  };
}

interface PendingPair {
  id: string;
  label: string;
  source: JumpPoint | null;
  target: JumpPoint | null;
  order: number;
}

/**
 * Normalisiert beliebige (auch alte) Marker-Daten in das Paar-Format.
 * Unterstützt das Altformat `{type: "start"|"end", tag, pageNumber, x, y}`,
 * das zu Paaren mit der ID `legacy-<tag>` zusammengeführt wird.
 */
export function normalizeJumpMarkers(input: unknown): JumpMarker[] {
  if (!Array.isArray(input)) return [];

  const pairs: PendingPair[] = [];
  const legacyByTag = new Map<string, { start?: JumpPoint; end?: JumpPoint; order: number }>();

  input.forEach((marker: unknown, idx) => {
    if (!marker || typeof marker !== "object") return;
    const raw = marker as Record<string, unknown>;

    if (raw.source || raw.target) {
      const source = normalizePoint(raw.source, "source");
      const target = normalizePoint(raw.target, "target");
      if (!source && !target) return;
      pairs.push({
        id: typeof raw.id === "string" ? raw.id.trim() : "",
        label: typeof raw.label === "string" ? raw.label.trim() : "",
        source,
        target,
        order: idx,
      });
      return;
    }

    const type = String(raw.type ?? "").toLowerCase();
    if (type !== "start" && type !== "end") return;
    const tag = String(raw.tag ?? "").trim();
    if (!tag) return;
    const point = normalizePoint(raw, type === "start" ? "source" : "target");
    if (!point) return;

    const entry = legacyByTag.get(tag) ?? { order: idx };
    entry.order = Math.min(entry.order, idx);
    if (type === "start") entry.start = point;
    else entry.end = point;
    legacyByTag.set(tag, entry);
  });

  for (const [tag, entry] of legacyByTag) {
    if (!entry.start && !entry.end) continue;
    pairs.push({
      id: `legacy-${tag}`,
      label: "",
      source: entry.start ?? null,
      target: entry.end ?? null,
      order: entry.order,
    });
  }

  pairs.sort((a, b) => a.order - b.order);

  const usedIds = new Set<string>();
  return pairs.map((pair, idx) => {
    const baseId = (pair.id && !usedIds.has(pair.id) ? pair.id : `jump-${idx + 1}`).slice(0, 56);
    let id = baseId;
    for (let counter = 2; usedIds.has(id); counter++) id = `${baseId}-${counter}`;
    usedIds.add(id);
    return {
      id,
      label: (pair.label || `Sprung ${idx + 1}`).slice(0, 80),
      source: pair.source,
      target: pair.target,
    };
  });
}
